import type {
  Column,
  ColumnSettings,
  EnumDef,
  Group,
  Project,
  Relationship,
  RefType,
  Schema,
  Table,
} from './types';

let counter = 0;

/** Unique-enough id generator: monotonic counter + wall-clock + random salt (ids are shared across users on the server). */
export function genId(prefix: string): string {
  counter += 1;
  const salt = Math.random().toString(36).slice(2, 6);
  return `${prefix}_${counter.toString(36)}_${Date.now().toString(36)}${salt}`;
}

/** Rotated across newly created groups so distinct domains are visually distinguishable
 * on the diagram by default, rather than every group sharing the same fallback hue. */
export const GROUP_COLOR_PALETTE = [
  '#6366f1', // indigo
  '#0ea5e9', // sky
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#ef4444', // red
  '#14b8a6', // teal
];

export function createEmptySchema(): Schema {
  return { tables: [], relationships: [], enums: [], groups: [] };
}

export function createColumn(name: string, type: string, settings: ColumnSettings = {}): Column {
  return { id: genId('col'), name, type, settings };
}

export function createTable(name: string, columns: Column[] = []): Table {
  return {
    id: genId('tbl'),
    name,
    columns: columns.length ? columns : [createColumn('id', 'uuid', { pk: true })],
    groupId: null,
  };
}

export function createGroup(name: string, tableIds: string[] = [], color?: string): Group {
  return { id: genId('grp'), name, tableIds, color };
}

export function createEnum(name: string, values: string[] = []): EnumDef {
  return {
    id: genId('enum'),
    name,
    values: values.map((v) => ({ id: genId('enumval'), name: v })),
  };
}

export function createRelationship(
  fromTableId: string,
  fromColumnId: string,
  toTableId: string,
  toColumnId: string,
  type: RefType = '>',
): Relationship {
  return { id: genId('rel'), fromTableId, fromColumnId, toTableId, toColumnId, type };
}

export function findTable(schema: Schema, tableId: string): Table | undefined {
  return schema.tables.find((t) => t.id === tableId);
}

export function findTableByName(schema: Schema, name: string): Table | undefined {
  return schema.tables.find((t) => t.name.toLowerCase() === name.toLowerCase());
}

export function findColumn(schema: Schema, tableId: string, columnId: string): Column | undefined {
  return findTable(schema, tableId)?.columns.find((c) => c.id === columnId);
}

export function findColumnByName(table: Table, name: string): Column | undefined {
  return table.columns.find((c) => c.name.toLowerCase() === name.toLowerCase());
}

export function schemaStats(schema: Schema) {
  return {
    tableCount: schema.tables.length,
    columnCount: schema.tables.reduce((sum, t) => sum + t.columns.length, 0),
    relationshipCount: schema.relationships.length,
    groupCount: schema.groups.length,
  };
}

export function createProject(name: string, databaseType: Project['databaseType'] = 'postgresql'): Project {
  const now = new Date().toISOString();
  return {
    id: genId('proj'),
    name,
    databaseType,
    dbmlSource: '',
    schema: createEmptySchema(),
    createdAt: now,
    updatedAt: now,
  };
}
